import { theme } from "../../theme";
import { scoreBand, SCORE_BAND_LABELS } from "../../lib/portfolioStatus";
import type { Assignee } from "./workspace";

// ── Shared types and helpers for the consultant workspace ─────────────────

/** One organisation in GET /consultant/portfolio. */
export interface OrgSummary {
  id: string;
  name: string;
  sector: string | null;
  size: string | null;
  dpTier: string | null;
  geography: string | null;
  serviceTier: { id: string; name: string; level: number; basePlatformFeePence: number };
  smallBusinessPackage: boolean;
  bundleTier: string | null;
  bureau: string | null;
  /** The client's SOA score — the figure on their own sidebar (backend lib/frameworkScores.ts). */
  latestScore: number | null;
  /** The last AI compliance check's overall score, shown only where labelled as such. */
  lastCheckScore: number | null;
  lastChecked: string | null;
  soaStats: { compliant: number; partial: number; gap: number };
  openFindings: number;
  pendingTierRequests: number;
  /** The tier the latest pending request asks for. */
  requestedTierName?: string | null;
  /** Firm revenue: null unless the viewer holds firm:admin. */
  monthlyBilledPence: number | null;
  monthlyAbacasRevenuePence: number | null;
  /** The consultancy allocation (days x rate): the amount billed for consultancy time. */
  monthlyConsultancyRevenuePence: number | null;
  /** Confirmed bookings and logged time this month, shown beside the allocation. */
  monthlyConsultancyDeliveredPence?: number | null;
  consultancyDeliveryDifferencePence?: number | null;
  consultancyDeliveryFlag?: "none" | "below" | "above" | null;
  sectorCode?: string | null;
  /** False while the regulated sector is unset or ambiguous (lib/sectors.ts). */
  sectorSettled?: boolean;
  /** Consultants assigned to the client, lead first (lib/clientAssignments.ts). */
  assignees?: Assignee[];
  frameworks: Array<{ frameworkId: string }>;
  userCount: number;
}

export interface PortfolioSummary {
  total: number;
  avgScore: number | null;
  /** Clients needing action (lib/portfolioStatus.ts clientNeedsAction). */
  needsAttention: number;
  /** Clients scoring 80% or more — a score band, not a certification. */
  strongCount: number;
  /** Null unless the viewer holds firm:admin. */
  totalMonthlyBilledPence: number | null;
  totalMonthlyAbacasRevenuePence: number | null;
  totalMonthlyConsultancyRevenuePence: number | null;
}

export interface PortfolioResponse {
  organizations: OrgSummary[];
  summary: PortfolioSummary;
  selfOrgId: string | null;
}

/** One organisation in the light GET /consultant/clients list. */
export interface ClientLite {
  id: string;
  name: string;
  sector: string | null;
  createdAt: string;
  tier: string;
  frameworks: string[];
  latestScore: number | null;
  userCount: number;
}

/** The consultant workspace route ids (one per nested route). */
export type ConsultantTab =
  | "dashboard" | "clients" | "calendar" | "findings" | "actions" | "activity"
  | "reports" | "billing" | "rates" | "invitations" | "hardening" | "groups"
  | "utilisation" | "time" | "revenue"
  | "whats-new" | "platform";

export function scoreColor(score: number | null): string {
  const band = scoreBand(score);
  if (band === "unassessed") return theme.color.faint;
  if (band === "compliant" || band === "strong") return theme.color.successText;
  if (band === "partial") return theme.color.warningText;
  return theme.color.dangerText;
}

export function scoreLabel(score: number | null): string {
  return SCORE_BAND_LABELS[scoreBand(score)];
}
